import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { List, styled, TypeText, Typography, TypographyPropsColorOverrides, TypographyPropsVariantOverrides } from "@mui/material";
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { TypographyVariant } from "@mui/material/styles/createTypography";
import { OverridableStringUnion } from '@mui/types';
import { SEARCH_DELAY_DEBOUNCE } from 'app/appConstants';
import axios from 'axios';
import { MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { IconButtonClearSearch } from 'components/iconButtons/IconButtonClearSearch';
import { TextFieldFilter } from "components/MprcAdvFilter";
import { MprcAlertNoRecordDrawer } from 'components/MprcAlertNoRecordDrawer';
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { ManagedUserPermissionsQueryResponse, PermissionConstraintDto } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getConstraintTypeNames } from "views/foundation/security/Home/EditRole/EditRoleHelpers";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    title?: string;
    subtitle?: string;
    userId: string;
    onClose: () => void;
}

type ViewDataType = ManagedUserPermissionsQueryResponse & {
    expanded: boolean;
};

/////////////////////////////////////////////////////////////////////////////////

interface IState {
    searchText: string;
    loading: boolean;
}

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion
        disableGutters
        elevation={0}
        square
        {...props}
    />
))(({ theme }) => ({
    '&:before': {
        display: 'none',
    },

}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    minHeight: "unset",
    margin: 0,
    padding: 0
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: 0
}));

interface RenderConstraintsPros {
    constraints: PermissionConstraintDto;
    variant?: OverridableStringUnion<TypographyVariant | 'inherit', TypographyPropsVariantOverrides>;
    color?: OverridableStringUnion<
        | 'primary'
        | 'secondary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning'
        | `text${Capitalize<keyof TypeText>}`,
        TypographyPropsColorOverrides>;
    spacing?: number;
    bold?: boolean;
}

export const RenderConstraints: React.FC<RenderConstraintsPros> = (props) => {

    const { constraints, variant, color, spacing = 0, bold = false } = props;
    const { t } = useTranslation();
    const names = getConstraintTypeNames(t);

    return (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {
                constraints.global === true
                && <li><Typography variant={variant ?? "caption"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{names["global"]}</Typography></li>
            }

            {
                constraints.directSubordinates === true
                && <li><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{names["directSubordinates"]}</Typography></li>
            }

            {
                constraints.allSubordinates === true
                && <li><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{names["allSubordinates"]}</Typography></li>
            }

            {
                constraints.selfUserOwnedObjects === true
                && <li><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{names["selfUserOwnedObjects"]}</Typography></li>
            }

            {
                constraints.selfUser === true
                && <li><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{names["selfUser"]}</Typography></li>
            }

            {
                constraints.selfUserUnitTypes?.map((item, index) => (
                    <li key={index}><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{`${names["selfUserUnitTypes"]}: ${item.name}`}</Typography></li>
                ))
            }

            {
                constraints.groups?.map((item, index) => (
                    <li key={index}><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{`${names["groups"]}: ${item.name}`}</Typography></li>
                ))
            }

            {
                constraints.units?.map((item, index) => (
                    <li key={index}><Typography variant={variant ?? "bodyNotice"} color={color} fontWeight={bold ? "bold" : undefined} paddingBottom={spacing}>{`${names["units"]}: ${item.name}${item.includeDescendants ? "[+]" : ""}`}</Typography></li>
                ))
            }
        </ul>
    );
};

export const UserPermissionListDrawer: React.FC<IPros> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation();

    const [viewData, setViewData] = useState<ViewDataType[] | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const searchTextRef = useRef<HTMLInputElement>(undefined);

    const [viewFilter, setViewFilter] = useState<IState>({ loading: true, searchText: "" });
    const [viewError, setViewError] = useState<any>();
    const abortControllerRef = useRef<AbortController>(undefined);

    const { loading } = viewFilter;
    const { title, subtitle, userId, onClose } = props;

    const handleChange = (item: ViewDataType) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        var x = viewData?.find(p => p.permissionId == item.permissionId);
        if (x) {
            x.expanded = isExpanded;
            setViewData([...viewData!]);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {

        async function getData() {

            abortControllerRef.current?.abort();

            abortControllerRef.current = new AbortController();

            try {
                var result = await api.v1.userManagement.getPermissionsList({ userId, searchText: viewFilter.searchText }, { signal: abortControllerRef.current.signal });
                setViewData(result.data.map((v) => ({ ...v, expanded: false } as ViewDataType)));
                setViewError(null);
            }
            catch (e) {
                if (!axios.isCancel(e)) {
                    setViewError(e);
                    setViewData(null);
                }
            }
            finally {
                setViewFilter({ ...viewFilter, loading: false });
                setTimeout(() => { searchTextRef.current?.focus(); }, 100);
            }
        }

        if (loading) getData();

    }, [loading]);

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setViewFilter({ ...viewFilter, searchText, loading: true });
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcBoxDrawer
            onClose={() => onClose()}
            title={title || t("User.UserPermissionListDrawer.Title")}
            subtitle={subtitle}
            header={
                <TextFieldFilter
                    autoFocus
                    fullWidth
                    autoComplete="off"
                    placeholder={t("User.UserPermissionListDrawer.SearchTextPlaceholder")}
                    value={searchText}
                    disabled={loading}
                    onKeyDown={(e) => e.keyCode == 13 && searchText.length > 0 && setViewFilter({ ...viewFilter, searchText, loading: true })}
                    onChange={() => {
                        setSearchText(searchTextRef.current?.value ?? "");
                    }}
                    inputRef={searchTextRef}
                    slotProps={{
                        input: {
                            endAdornment:
                                searchText != "" &&
                                <IconButtonClearSearch
                                    size="small"
                                    onClick={() => setSearchText("")}
                                />
                        }
                    }}
                />
            }

            content={
                <>
                    <MprcRequestErrorReport error={viewError} />

                    {
                        loading ?
                            <MprcSpinnerIndicator />
                            : !viewError && viewData?.length === 0 && searchText.length == 0
                                ? <MprcAlertNoRecordDrawer message={t("User.UserPermissionListDrawer.ThereAreNoResults")} />
                                : !viewError && viewData?.length === 0 && searchText.length > 0
                                    ? <MprcAlertNoRecordDrawer message={t("User.UserPermissionListDrawer.NoRecordsFoundMessage")} />
                                    : null
                    }

                    {
                        !viewError && viewData && viewData.length > 0 &&
                        <List disablePadding>
                            {
                                viewData!.map((v) => (
                                    <Accordion key={v.permissionId} expanded={v.expanded} onChange={handleChange(v)} sx={{}}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="panel1bh-content"
                                            id="panel1bh-header"
                                        >
                                            <Typography variant="h6" color="muted.dark" fontWeight="bold">
                                                {v.name}
                                            </Typography>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <RenderConstraints constraints={v.constraints} />
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                        </List>
                    }
                </>
            }
        />
    );
};
